import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { Printer } from "lucide-react";
import { Button } from "@eduviaos/ui-kit";
import type { Institution } from "../lib/institution";
import { fetchSkillCertificateVerify } from "../lib/learning/certificateVerify";
import { usePortalLocale } from "../lib/i18n/usePortalLocale";
import { CertificateDocument } from "./sc/CertificateDocument";

export function CertificateVerifyPage({ institution }: { institution: Institution }) {
  const { code = "" } = useParams();
  const { strings: s, locale } = usePortalLocale(false);
  const verifyQuery = useQuery({
    queryKey: ["public-skill-certificate", code],
    queryFn: () => fetchSkillCertificateVerify(code),
    enabled: Boolean(code),
    retry: false,
  });

  const cert = verifyQuery.data;

  return (
    <div className="portal-aurora bg-background text-foreground min-h-dvh px-4 py-8 print:bg-white print:px-0">
      <div className="mx-auto flex max-w-xl flex-col gap-4">
        {verifyQuery.isPending && (
          <div
            aria-busy="true"
            className="border-border/60 bg-card h-[28rem] animate-pulse rounded-[1.5rem] border"
          />
        )}
        {verifyQuery.isError && (
          <p role="alert" className="text-destructive text-sm">
            {s.certificateVerifyFailed}
          </p>
        )}
        {cert && !cert.valid && (
          <p role="alert" className="text-destructive text-sm">
            {s.certificateInvalid}
          </p>
        )}
        {cert?.valid && (
          <>
            <CertificateDocument
              institution={institution}
              cert={cert}
              verifyCode={code}
              strings={s}
              locale={locale}
            />
            <Button
              type="button"
              variant="outline"
              className="w-full sm:w-auto sm:self-center print:hidden"
              onClick={() => window.print()}
            >
              <Printer className="mr-2 size-4" aria-hidden />
              {s.printCertificate}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
